import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Input, Button, Container, Row, Col, CardBody, Nav, TabPane } from "reactstrap";
import userImage1 from '../../../assets/images/5.avif';
import adminImage from "../../../assets/images/favicon.ico";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import SDK from "../../../config";
import moment from "moment";
import socketIO from "socket.io-client";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageSection from '../../../components/Loader/loader';

const ChatData = () => {
  const [curMessage, setCurMessage] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUserImage, setSelectedUserImage] = useState(userImage1);
  const [selectedDeviceID, setSelectedDeviceID] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [allDevices, setAllDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [devicesInitialLoader, setDevicesInitialLoader] = useState(true);
  const [devicesScrollLoader, setDevicesScrollLoader] = useState(false);
  const [initialLoader, setInitialLoader] = useState(false);
  const [scrollLoader, setScrollLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastOpenedChat, setLastOpenedChat] = useState(null);
  const [messageOffset, setMessageOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [devicesOffset, setDevicesOffset] = useState(0);
  const [devicesLimit] = useState(30);
  const [hasMoreDevices, setHasMoreDevices] = useState(true);

  const [breadcrumbItems] = useState([
    { title: "Minimis", link: "/" },
    { title: "Chat", link: "#" },
  ]);

  const socket = socketIO(SDK.SOCKET_URL);
  const chatContainerRef = useRef(null);
  const devicesContainerRef = useRef(null);

  useEffect(() => {
    socket.emit("ios-all-devices", { offset: devicesOffset, limit: devicesLimit });

    socket.on("iOS-All-Devices-Response", (fetchedDevices) => {
      
      setAllDevices((prevDevices) => {
        const newDevices = fetchedDevices.filter(device => !prevDevices.some(prevDevice => prevDevice.deviceID === device.deviceID));
        return [...prevDevices, ...newDevices];
      });

      setFilteredDevices((prevDevices) => {
        const newDevices = fetchedDevices.filter(device => !prevDevices.some(prevDevice => prevDevice.deviceID === device.deviceID));
        return [...prevDevices, ...newDevices];
      });

      if (devicesOffset === 0) {
        setDevicesInitialLoader(false);
      }

      if (fetchedDevices.length < devicesLimit) {
        setHasMoreDevices(false);
      }

      setDevicesScrollLoader(false);
    });

    return () => {
      socket.off("iOS-All-Devices-Response");
    };
  }, [devicesOffset, devicesLimit]);

  useEffect(() => {
    const handleUpdatedDevices = (devices) => {
      setAllDevices(devices);
      setFilteredDevices(devices);
    };

    socket.on('iOS-All-Devices-Response', handleUpdatedDevices);

    return () => {
      socket.off('iOS-All-Devices-Response', handleUpdatedDevices);
    };
  }, []);

  const handleScrollDevices = () => {
    if (devicesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = devicesContainerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 10 && hasMoreDevices && !devicesScrollLoader) {
        setDevicesScrollLoader(true);
        setDevicesOffset((prevOffset) => prevOffset + devicesLimit);
        socket.emit("ios-all-devices", { offset: devicesOffset + devicesLimit, limit: devicesLimit });
      }
    }
  };

  useEffect(() => {
    const deviceContainer = devicesContainerRef.current;
    if (deviceContainer) {
      deviceContainer.addEventListener('scroll', handleScrollDevices);
    }

    return () => {
      if (deviceContainer) {
        deviceContainer.removeEventListener('scroll', handleScrollDevices);
      }
    };
  }, [hasMoreDevices, devicesOffset, devicesScrollLoader]);


  useEffect(() => {
    if (selectedEmail) {
      setInitialLoader(true);

      // Emit socket events to join room and load chat history
      socket.emit('ios-join-room', { email: selectedEmail });
      socket.emit('ios-chat-history', { email: selectedEmail, offset: messageOffset, limit: 10 });

      // Handle chat history response
      const handleChatHistory = (messages) => {
        setInitialLoader(false);
        if (Array.isArray(messages) && messages.length > 0) {
          setCurMessage((prevMessages) => [...messages, ...prevMessages]);
          if (messages.length < 10) {
            setHasMoreMessages(false);
          }
        } else {
          setHasMoreMessages(false);
        }
      };

      // Handle new message response
      const handleNewMessage = (message) => {
        setCurMessage((prevMessages) => [...prevMessages, message]);

        // Emit admin-open-chat on receiving a new message
        socket.emit('ios-admin-open-chat', { email: selectedEmail });

        // Reset unread counter for the open chat
        setFilteredDevices((prevDevices) =>
          prevDevices.map((device) =>
            device.email === selectedEmail ? { ...device, adminUnRead: 0 } : device
          )
        );
      };

      // Listen for chat history and new messages
      socket.emit('ios-admin-open-chat', { email: selectedEmail });
      socket.on('ios-chat-history', handleChatHistory);
      socket.on('ios-new-message', handleNewMessage);

      return () => {
        socket.off('ios-chat-history', handleChatHistory);
        socket.off('ios-new-message', handleNewMessage);
      };
    }
  }, [selectedEmail, messageOffset]);


  useEffect(() => {
    if (!isUserScrolling && !initialLoader && !messageOffset) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [curMessage, initialLoader]);


  // Chat scroll event to load more messages
  useEffect(() => {
    const handleScroll = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;

        if (scrollTop === 0 && hasMoreMessages) {
          const previousScrollHeight = chatContainerRef.current.scrollHeight;
          setScrollLoader(true);
          setMessageOffset((prevOffset) => prevOffset + 10);
          socket.emit('ios-chat-history', { email: selectedEmail, offset: messageOffset + 10, limit: 10 });
          const handleChatHistory = (messages) => {
            setScrollLoader(false);
            if (Array.isArray(messages) && messages.length > 0) {
              setCurMessage((prevMessages) => [...messages.reverse(), ...prevMessages]);

              setTimeout(() => {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - previousScrollHeight;
              }, 100);

              if (messages.length < 10) {
                setHasMoreMessages(false);
              }
            } else {
              setHasMoreMessages(false);
            }
          };

          socket.on('ios-chat-history', handleChatHistory);

          return () => {
            socket.off('ios-chat-history', handleChatHistory);
          };
        }
      }
    };

    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [hasMoreMessages, messageOffset, selectedEmail, scrollLoader]);


  const handleChatItemClick = (email, deviceID, userName) => {
    setCurMessage([]);
    setSelectedDeviceID(deviceID);
    setSelectedEmail(email);
    setSelectedUserName(userName);
    setLastOpenedChat(deviceID);
    setInitialLoader(true);
    setMessageOffset(0);
    setHasMoreMessages(true);
  };

  const getSortedDevices = () => {
    return filteredDevices
      .map(device => ({
        ...device,
        adminUnRead: device.deviceID === lastOpenedChat ? 0 : device.adminUnRead
      }))
      .sort((a, b) => {
        if (a.deviceID === lastOpenedChat) return -1;
        if (b.deviceID === lastOpenedChat) return 1;
        return b.adminUnRead - a.adminUnRead;
      });
  };


  const sendMessage = () => {
    if (newMessage.trim() === "") {
      return;
    }

    const data = {
      email: selectedEmail,
      deviceID: selectedDeviceID,
      message: newMessage,
      isUser: false,
    };

    socket.emit("ios-send-message", data);
    setNewMessage("");
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = allDevices.filter(device =>
      device.email.toLowerCase().includes(query) ||
      device.deviceName.toLowerCase().includes(query)
    );
    setFilteredDevices(filtered);
  };


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Chat" breadcrumbItems={breadcrumbItems} />
          <div className="d-lg-flex mb-4">
            <div className="chat-leftsidebar">
              <CardBody className="border-bottom py-2">
                <div className="search-box chat-search-box">
                  <div className="position-relative">
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </div>
              </CardBody>
              <div className="chat-leftsidebar-nav">
                <Nav pills justified></Nav>
              </div>
              <section className="py-3">
                <TabPane>
                  <div>
                    <h5 className="font-size-14 px-3">Recent</h5>
                    <hr />
                    <div className="chat-conversation p-3" ref={devicesContainerRef} style={{ maxHeight: "450px", overflowY: "auto" }}>
                      <ul className="list-unstyled chat-list">
                        <PerfectScrollbar style={{ maxHeight: "100%" }}>
                          {devicesInitialLoader ? (
                            <div className="chat-conversation p-3 chat-conversation-height" style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%"
                            }}>
                              <PageSection />
                            </div>
                          ) : (
                            <>
                              {getSortedDevices().map((device, key) => (
                                <div className="d-flex" key={key}>
                                  <img src={selectedUserImage} className="rounded-circle avatar-xs ms-3" alt="avatar" />
                                  <li
                                    onClick={() => handleChatItemClick(device.email, device.deviceID, device.deviceName, device.userImage)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <div className="mb-1 mx-4" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                      <div className="flex-1 overflow-hidden">
                                        <h5 className="text-truncate font-size-13 mb-1">{device.email}</h5>
                                        <p className="text-muted mb-0">{device.deviceName}</p>
                                      </div>
                                      {device.adminUnRead > 0 && (
                                        <div className="badge rounded-pill bg-success ms-3">
                                          {device.adminUnRead}
                                        </div>
                                      )}
                                    </div>
                                    <hr />
                                  </li>
                                </div>
                              ))}

                              {devicesScrollLoader && (
                                <div style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  padding: "0px 0px 30px 0px"
                                }}>
                                  <PageSection />
                                </div>
                              )}
                            </>
                          )}
                        </PerfectScrollbar>
                      </ul>
                    </div>
                  </div>
                </TabPane>
              </section>
            </div>
            <div className="w-100 user-chat mt-4 mt-sm-0" style={{ position: 'relative' }}>
              <div className="p-3 px-lg-4 user-chat-border">
                {selectedUserName && (
                  <div className="d-flex align-items-center">
                    <div>
                      <img src={selectedUserImage} className="rounded-circle avatar-xs" alt="avatar" />
                    </div>
                    <div className="ms-3">
                      <h5 className="font-size-15 mb-1 text-truncate">{selectedEmail}</h5>
                      <p className="text-muted text-truncate mb-0">{selectedUserName}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="px-lg-2">
                <div className="chat-conversation p-3" ref={chatContainerRef} style={{ maxHeight: "450px", overflowY: "auto" }}>
                  <ul className="list-unstyled mb-0 pe-3">
                    <PerfectScrollbar style={{ maxHeight: "100%" }}>
                      {initialLoader && curMessage.length === 0 ? (
                        <div className="chat-conversation p-3 chat-conversation-height" style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%"
                        }}>
                          <PageSection style={{}} />
                        </div>
                      ) : (
                        <>
                          {scrollLoader && hasMoreMessages && curMessage.length > 0 && (
                            <div className="" style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%"
                            }}>
                              <PageSection style={{}} />
                            </div>
                          )}
                          {curMessage.map((message, key) => (
                            <li key={key} className={message.isUser === true ? "" : "right"} style={{ paddingRight: "20px" }}>
                              <div className="conversation-list">
                                <div className="chat-avatar">
                                  <img
                                    src={message.isUser === true ? selectedUserImage : adminImage}
                                    alt=""
                                  />
                                </div>
                                <div className="ctext-wrap">
                                  <div className="conversation-name">{message.isUser === true ? "User" : "Admin"}</div>
                                  <div className="ctext-wrap-content">
                                    <p className="mb-0">{message.message}</p>
                                  </div>
                                  <p className="chat-time mb-0">{moment(message.createdAt).format("hh:mm")}</p>
                                </div>
                              </div>
                            </li>
                          ))}
                        </>
                      )}
                    </PerfectScrollbar>
                  </ul>
                </div>
              </div>

              <div className="px-lg-3">
                <div className="chat-input-section" style={{ position: 'absolute', width: '94%', bottom: '30px' }}>
                  <Row >
                    <Col>
                      <div className="position-relative">
                        <Input
                          type="text"
                          className="form-control chat-input"
                          placeholder="Enter Message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              sendMessage();
                            }
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={{ size: "auto" }}>
                      <Button
                        type="button"
                        color="primary"
                        className="btn-rounded chat-send w-md"
                        onClick={sendMessage}
                      >
                        <span className="d-none d-sm-inline-block me-2">Send</span>
                        <i className="mdi mdi-send"></i>
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default connect(null, null)(ChatData);
